import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useSuiteList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refSuitesTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'id',
      sortable: true,
    },
    {
      key: 'image',
      label: 'Image',
      sortable: false,
    },
    {
      key: 'description',
      label: 'suite name',
      sortable: true,
    },
    {
      key: 'code',
      label: 'suite code',
      sortable: true,
    },
    {
      key: 'location',
      label: 'location',
      sortable: false,
    },
    {
      key: 'productType',
      label: 'product Type',
      sortable: true,
    },
    {
      key: 'reservableBy',
      label: 'reservable By',
      sortable: true,
    },
    {
      key: 'saleStatus',
      label: 'sale Status',
      sortable: true,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalSuites = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc !== 'false') : true)
  const locationFilter = ref([])
  if (Array.isArray(route.query.location)) {
    locationFilter.value = route.query.location
  } else if (route.query.location) {
    locationFilter.value = [route.query.location]
  }
  const productFilter = ref([])
  if (Array.isArray(route.query.product)) {
    productFilter.value = route.query.product
  } else if (route.query.product) {
    productFilter.value = [route.query.product]
  }
  const reservableFilter = ref([])
  if (Array.isArray(route.query.reservable)) {
    reservableFilter.value = route.query.reservable
  } else if (route.query.reservable) {
    reservableFilter.value = [route.query.reservable]
  }
  const saleStatusFilter = ref([])
  if (Array.isArray(route.query.saleStatus)) {
    saleStatusFilter.value = route.query.saleStatus
  } else if (route.query.saleStatus) {
    saleStatusFilter.value = [route.query.saleStatus]
  }
  const toDeletedID = ref(null)
  const currentDataLength = ref(0)
  const itemListData = ref([])
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refSuitesTable.value ? refSuitesTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalSuites.value,
    }
  })

  const refetchData = () => {
    refSuitesTable.value.refresh()
  }

  // watch([searchQuery, idSearchQuery, statusFilter, createdAtFilter], () => {
  //   currentPage.value = 1
  //   refetchData()
  // }, { deep: true })

  // watch([currentPage], () => {
  //   refetchData()
  // }, { deep: true })

  const fetchSuites = () => {
    store
      .dispatch('app-suites/fetchSuites', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        location: locationFilter.value,
        product: productFilter.value,
        reservable: reservableFilter.value,
        saleStatus: saleStatusFilter.value,
      },
      { root: true })
      .then(response => {
        const { suites, total } = response.data
        itemListData.value = suites
        currentDataLength.value = suites.length
        // callback(locations)
        totalSuites.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              location: locationFilter.value,
              product: productFilter.value,
              reservable: reservableFilter.value,
              saleStatus: saleStatusFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  let timer = null
  watch([locationFilter, productFilter, reservableFilter, saleStatusFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchSuites()
    }, 1000)
  }, { deep: true })

  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchSuites()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchSuites()
  }, { deep: true })

  fetchSuites()

  const deleteSuite = () => {
    store
      .dispatch('app-suites/deleteSuite', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        fetchSuites()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveSaleStatusVariant = status => {
    if (status === 'Open Sales') return 'success'
    if (status === 'Not for sale') return 'secondary'
    if (status === 'Only from reservations') return 'warning'
    return 'primary'
  }

  const resolveReservableVariant = status => {
    if (status === 'Staff-only') return 'warning'
    if (status === 'Not for reservation') return 'secondary'
    if (status === 'All agents & Staff') return 'success'
    return 'primary'
  }

  return {
    fetchSuites,
    tableColumns,
    perPage,
    currentPage,
    totalSuites,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refSuitesTable,

    resolveSaleStatusVariant,
    resolveReservableVariant,
    refetchData,

    toDeletedID,
    deleteSuite,
    currentDataLength,
    itemListData,

    // Extra Filters
    locationFilter,
    productFilter,
    reservableFilter,
    saleStatusFilter,
  }
}
